// components/models/MagnificVideoUpscaleControlPanel.tsx
import React, { useCallback } from 'react';
import Select from '../ui/Select';
import RangeControl from '../ui/RangeControl';
import Switch from '../ui/Switch';
import VideoInputRangeControls from './VideoInputRangeControls';
import {
  MagnificVideoUpscaleFlavor,
  MagnificVideoUpscaleParams,
  MagnificVideoUpscaleResolution,
} from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import { numberOrDefault } from '#lib/utils';

interface MagnificVideoUpscaleControlPanelProps {
  params: MagnificVideoUpscaleParams;
  setModelParams: (params: (prevParams: { [model: string]: MagnificVideoUpscaleParams }) => { [model: string]: MagnificVideoUpscaleParams }) => void;
  diffusionModel: string;
}

const defaultMagnificVideoUpscaleParams: MagnificVideoUpscaleParams = {
  prompt: '',
  resolution: '2k',
  creativity: 0,
  fps_boost: false,
  sharpen: 0,
  smart_grain: 0,
  flavor: 'vivid',
  video_input_start_ms: null,
  video_input_end_ms: null,
  seed: -1,
};

export const getDefaultMagnificVideoUpscaleParams = (): MagnificVideoUpscaleParams => ({
  ...defaultMagnificVideoUpscaleParams,
});

const resolutionOptions: MagnificVideoUpscaleResolution[] = ['720p', '1k', '2k', '4k'];
const flavorOptions: MagnificVideoUpscaleFlavor[] = ['vivid', 'natural'];

const MagnificVideoUpscaleControlPanel: React.FC<MagnificVideoUpscaleControlPanelProps> = React.memo(({
  params,
  setModelParams,
  diffusionModel,
}) => {
  const { kernel } = useKernel();

  const updateParams = useCallback((
    name: keyof MagnificVideoUpscaleParams,
    value: string | number | boolean | null
  ) => {
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[MagnificVideoUpscaleControlPanel] Missing params for model "${diffusionModel}".`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: value,
        },
      };
    });

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value,
      model_name: diffusionModel,
      source_component: 'MagnificVideoUpscaleControlPanel',
    });
  }, [diffusionModel, kernel, setModelParams]);

  const handleResolutionChange = useCallback((value: string) => {
    updateParams('resolution', value as MagnificVideoUpscaleResolution);
  }, [updateParams]);

  const handleFlavorChange = useCallback((value: string) => {
    updateParams('flavor', value as MagnificVideoUpscaleFlavor);
  }, [updateParams]);

  const handleRangeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    updateParams(name as keyof MagnificVideoUpscaleParams, Number(value));
  }, [updateParams]);

  const handleFpsBoostToggle = useCallback(() => {
    const live = kernel.current?.getModelParams()[diffusionModel] as MagnificVideoUpscaleParams | undefined;
    updateParams('fps_boost', !(live?.fps_boost === true));
  }, [diffusionModel, kernel, updateParams]);

  const handleVideoInputRangeChange = useCallback((
    name: 'video_input_start_ms' | 'video_input_end_ms',
    value: number | null,
  ) => {
    updateParams(name, value);
  }, [updateParams]);

  return (
    <div className="space-y-4">
      <Select
        label="Output Resolution"
        value={params.resolution ?? '2k'}
        options={resolutionOptions}
        onChange={handleResolutionChange}
      />
      <Select
        label="Flavor"
        value={params.flavor ?? 'vivid'}
        options={flavorOptions}
        onChange={handleFlavorChange}
      />
      <RangeControl
        label="Creativity"
        name="creativity"
        min={0}
        max={100}
        step={1}
        value={numberOrDefault(params.creativity, 0)}
        onChange={handleRangeChange}
      />
      <RangeControl
        label="Sharpen"
        name="sharpen"
        min={0}
        max={100}
        step={1}
        value={numberOrDefault(params.sharpen, 0)}
        onChange={handleRangeChange}
      />
      <RangeControl
        label="Smart Grain"
        name="smart_grain"
        min={0}
        max={100}
        step={1}
        value={numberOrDefault(params.smart_grain, 0)}
        onChange={handleRangeChange}
      />
      <VideoInputRangeControls
        startMs={params.video_input_start_ms}
        endMs={params.video_input_end_ms}
        onChange={handleVideoInputRangeChange}
      />
      <div className="flex items-center space-x-2">
        <Switch
          checked={params.fps_boost === true}
          onCheckedChange={handleFpsBoostToggle}
          labelRight="FPS Boost"
        />
      </div>
    </div>
  );
});

MagnificVideoUpscaleControlPanel.displayName = 'MagnificVideoUpscaleControlPanel';

export default MagnificVideoUpscaleControlPanel;
